import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useTeacherAuth } from '../context/TeacherAuthContext';
import {
  getClassReinforcementReport,
  getReinforcementFlags,
  getReinforcementTrend,
  ReinforcementFlag,
  ReinforcementTrend,
  ClassReinforcementResponse,
} from '../services/api';
import { ReinforcementStudent } from '../types';
import { resolveTeacherClassContext } from '../utils/teacherClassContext';
import TeacherLayout from './teacher/TeacherLayout';
import ClassAnalytics from './teacher/ClassAnalytics';
import {
  classHealthBySubject,
  rollupBucket,
  subjectLabel,
} from './teacher/SubjectHealthRows';
import DateRangeControl, { DateRange } from './teacher/DateRangeControl';

// --- Small SVG glyphs for the KPI cards -------------------------------------

const Glyph: React.FC<{ d: string }> = ({ d }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d={d} />
  </svg>
);

const GLYPHS = {
  users: 'M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8 M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75',
  book: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20 M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z',
  check: 'M22 11.08V12a10 10 0 1 1-5.93-9.14 M22 4 12 14.01l-3-3',
  half: 'M12 2a10 10 0 1 0 10 10 M12 2v10h10',
  alert: 'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z M12 9v4 M12 17h.01',
  flag: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z M4 22v-7',
};

/** One stat card in the top row — icon chip, label, big number, sub-line. */
const KpiCard: React.FC<{
  glyph: keyof typeof GLYPHS;
  chip: string; // icon chip tone classes
  label: string;
  value: React.ReactNode;
  valueCls?: string;
  sub?: string;
}> = ({ glyph, chip, label, value, valueCls, sub }) => (
  <div className="rounded-2xl border border-[#E9E6F4] bg-white p-4">
    <div className="flex items-center gap-2.5">
      <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-xl ${chip}`}>
        <Glyph d={GLYPHS[glyph]} />
      </span>
      <span className="text-[13px] font-semibold text-slate-500">{label}</span>
    </div>
    <div className={`mt-2.5 text-3xl font-bold tabular-nums tracking-tight ${valueCls ?? 'text-slate-900'}`}>
      {value}
    </div>
    {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
  </div>
);

// Roll a student's topic states up to a single class-health bucket.
// Any not-yet topic dominates; else partly; else got; only-unassessed topics
// roll up to the neutral 'explored'. (Shared with TeacherDashboard via
// teacher/SubjectHealthRows.)
const studentHealth = (s: ReinforcementStudent) =>
  rollupBucket((s.topics || []).map((t) => t.state));

// Direction-C status: a subject/student's got·partly·not-yet spread → a
// triage label the teacher can scan. Not-yet-heavy = needs help; got-heavy =
// on track; only-unassessed = exploring; everything else = shaky.
type TriageStatus = 'ok' | 'shaky' | 'help' | 'explored';
const triageStatus = (g: { got: number; partly: number; notyet: number; explored: number }): TriageStatus => {
  const assessed = g.got + g.partly + g.notyet;
  if (assessed === 0) return 'explored';
  if (g.notyet / assessed >= 1 / 3) return 'help';
  if (g.got / assessed >= 0.6) return 'ok';
  return 'shaky';
};
const STATUS_META: Record<TriageStatus, { label: string; cls: string }> = {
  ok: { label: 'On track', cls: 'text-emerald-700 bg-emerald-50' },
  shaky: { label: 'Shaky', cls: 'text-amber-700 bg-amber-50' },
  help: { label: 'Needs help', cls: 'text-rose-700 bg-rose-50' },
  explored: { label: 'Exploring', cls: 'text-slate-500 bg-slate-100' },
};
const NCERT_SUBJECTS = new Set(['Maths', 'Science', 'Social']);

/** Which students sit behind one subject row, with their per-subject bucket
 *  and the topics that put them there. Powers the row's dropdown. */
const studentsForSubject = (
  students: ReinforcementStudent[],
  label: string
): { student_id: number; name: string; status: TriageStatus; topics: { topic: string; state: string }[] }[] => {
  const out: { student_id: number; name: string; status: TriageStatus; topics: { topic: string; state: string }[] }[] = [];
  for (const s of students) {
    const topics = (s.topics || []).filter((t) => subjectLabel(t.subject) === label);
    if (topics.length === 0) continue;
    const bucket = rollupBucket(topics.map((t) => t.state));
    const status: TriageStatus =
      bucket === 'got' ? 'ok' : bucket === 'partly' ? 'shaky' : bucket === 'notyet' ? 'help' : 'explored';
    out.push({
      student_id: s.student_id,
      name: s.name,
      status,
      topics: topics.map((t) => ({
        topic: t.topic,
        state:
          t.state === 'solid'
            ? 'solid'
            : t.state === 'partly_solid'
            ? 'shaky'
            : t.state === 'forgotten' || t.state === 'never_learned'
            ? 'not yet'
            : 'explored',
      })),
    });
  }
  // needs-help first so the teacher sees the urgent names without scanning.
  const rank: Record<TriageStatus, number> = { help: 0, shaky: 1, explored: 2, ok: 3 };
  return out.sort((a, b) => rank[a.status] - rank[b.status]);
};

const StatusPill: React.FC<{ status: TriageStatus }> = ({ status }) => {
  const m = STATUS_META[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${m.cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {m.label}
    </span>
  );
};

/** A slim got/partly/not-yet/explored stacked bar for one row. */
const MiniHealthBar: React.FC<{ g: { got: number; partly: number; notyet: number; explored: number } }> = ({ g }) => {
  const total = g.got + g.partly + g.notyet + g.explored || 1;
  const seg = (n: number, cls: string) =>
    n > 0 ? <span className={cls} style={{ width: `${(n / total) * 100}%` }} /> : null;
  return (
    <span className="flex h-2.5 overflow-hidden rounded-full bg-[#F1EEF9]">
      {seg(g.got, 'bg-emerald-500')}
      {seg(g.partly, 'bg-amber-500')}
      {seg(g.notyet, 'bg-rose-400')}
      {seg(g.explored, 'bg-slate-300')}
    </span>
  );
};

// --- Weekly "got it" trend ------------------------------------------------
// Headline "got it %" for the latest week plus week/month delta pills.
// Rendered as a plain block so the parent grid can card it next to Subject
// performance.

// A coloured delta pill: emerald up / rose down / slate flat.
const DeltaPill: React.FC<{ label: string; value: number }> = ({ label, value }) => {
  const rounded = Math.round(value);
  let tone: string;
  let text: string;
  if (rounded > 0) {
    tone = 'bg-emerald-100 text-emerald-800';
    text = `▲ +${rounded}%`;
  } else if (rounded < 0) {
    tone = 'bg-rose-100 text-rose-800';
    text = `▼ ${rounded}%`;
  } else {
    tone = 'bg-slate-100 text-slate-600';
    text = 'no change';
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span className="text-slate-400">{label}</span>
      <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-semibold ${tone}`}>
        {text}
      </span>
    </span>
  );
};

const TrendWidget: React.FC<{ trend: ReinforcementTrend }> = ({ trend }) => {
  const weeks = trend.weeks || [];

  // Honest empty state only when there's no weekly data at all. With a single
  // week we can still show this week's headline %; the week/month deltas stay
  // hidden until a second week exists to compare against.
  if (weeks.length === 0) {
    return (
      <p className="mt-3 text-sm text-slate-400">
        Weekly trend builds up as the class keeps using the curiosity. Check back next week.
      </p>
    );
  }

  // Headline: this week's share of students who've "got" the topics.
  const latest = weeks[weeks.length - 1];
  const gotPct = latest.total > 0 ? Math.round((latest.got / latest.total) * 100) : 0;
  const hasDeltas = trend.week_delta_got_pct != null || trend.month_delta_got_pct != null;

  return (
    <>
      {/* Big number — the one figure to read. */}
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-5xl font-bold tabular-nums tracking-tight text-emerald-600">{gotPct}%</span>
        <span className="text-sm font-medium text-slate-500">got it this week</span>
      </div>

      {/* Movement — appears once there's a prior week to compare against. */}
      {hasDeltas && (
        <div className="mt-4 flex flex-col gap-2">
          {trend.week_delta_got_pct != null && <DeltaPill label="vs last week" value={trend.week_delta_got_pct} />}
          {trend.month_delta_got_pct != null && (
            <DeltaPill label="over the month" value={trend.month_delta_got_pct} />
          )}
        </div>
      )}

      <p className="mt-4 text-xs text-slate-400">
        {weeks.length > 1
          ? 'Share of students who\'ve "got" this week\'s topics, week by week.'
          : 'Share of students who\'ve "got" this week\'s topics. A week-on-week trend appears next week.'}
      </p>
    </>
  );
};

const ClassReinforcement: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useTeacherAuth();
  const { school, grade, section } = resolveTeacherClassContext((location.state as any) || {});
  const gradeNum = typeof grade === 'string' ? Number(grade) : grade;

  // Primary content is the reinforcement report; the old dashboard analytics
  // live under a second "Class analytics" section.
  const [section2, setSection2] = useState<'report' | 'analytics'>('report');

  const [resp, setResp] = useState<ClassReinforcementResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Download → open a print-ready report document and invoke the browser's
  // "Save as PDF". Built from the same data on screen. The real handler is
  // defined further down, once the derived report data is in scope.
  const [downloadNote, setDownloadNote] = useState<string | null>(null);
  const downloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashDownloadNote = useCallback((msg: string) => {
    setDownloadNote(msg);
    if (downloadTimer.current) clearTimeout(downloadTimer.current);
    downloadTimer.current = setTimeout(() => setDownloadNote(null), 5000);
  }, []);
  useEffect(
    () => () => {
      if (downloadTimer.current) clearTimeout(downloadTimer.current);
    },
    []
  );

  // Report window. Default "All time" (cumulative) — narrowing it recomputes
  // the report from just the conversations in that period.
  const [reportRange, setReportRange] = useState<DateRange>({ start: '', end: '' });
  // Which subject row's student-dropdown is open (one at a time).
  const [openSubject, setOpenSubject] = useState<string | null>(null);

  const load = useCallback(
    async (force = false) => {
      if (!token || grade == null) return;
      setLoading(true);
      setError(null);
      try {
        const data = await getClassReinforcementReport(
          token,
          Number(grade),
          section,
          force,
          reportRange.start || undefined,
          reportRange.end || undefined,
          school
        );
        setResp(data);
      } catch (e: any) {
        setError(e.message || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    },
    [token, school, grade, section, reportRange.start, reportRange.end]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const data = resp?.report_data;
  const topics = data?.topics || [];
  // Only claim "no data" once a report has actually come back — never while a
  // class is missing (load() bails early) or before the first fetch resolves.
  const isEmpty = resp != null && (resp.status === 'empty' || (!loading && topics.length === 0));

  // Per-student rows (v2). Undefined on old cached reports; guard everywhere.
  const students = data?.students;
  const hasStudents = !!students && students.length > 0;

  // ── Reinforcement flags ────────────────────────────────────────────────────
  // Active flags for this class, keyed as `${student_id}::${topic}` so rows can
  // show a "Flagged" state and skip obvious dupes.
  const [flags, setFlags] = useState<ReinforcementFlag[]>([]);

  // ── Weekly "got it" trend ───────────────────────────────────────────────────
  // Fetched independently of the report; degrades silently on error.
  const [trend, setTrend] = useState<ReinforcementTrend | null>(null);
  useEffect(() => {
    if (!token || grade == null) return;
    let mounted = true;
    getReinforcementTrend(token, Number(grade), section)
      .then((t) => mounted && setTrend(t))
      .catch(() => mounted && setTrend(null));
    return () => {
      mounted = false;
    };
  }, [token, grade, section]);


  useEffect(() => {
    if (!token || grade == null) return;
    let mounted = true;
    getReinforcementFlags(token, Number(grade), section)
      .then((r) => mounted && setFlags(r.flags ?? []))
      .catch(() => mounted && setFlags([]));
    return () => {
      mounted = false;
    };
  }, [token, grade, section]);


  const sortedStudents = useMemo(
    () => (students ? [...students].sort((a, b) => b.struggled_count - a.struggled_count) : []),
    [students]
  );

  // Whole-class traffic-light rollup from per-student topic states.
  const health = useMemo(() => {
    if (!hasStudents) return null;
    const counts = { got: 0, partly: 0, notyet: 0, explored: 0 };
    students!.forEach((s) => {
      counts[studentHealth(s)] += 1;
    });
    return { ...counts, total: students!.length };
  }, [hasStudents, students]);

  // Per-subject health + the subjects that most need attention (for the glance).
  const subjectHealth = useMemo(
    () => (hasStudents ? classHealthBySubject(students!) : []),
    [hasStudents, students]
  );
  const attentionSubjects = useMemo(
    () =>
      subjectHealth
        .filter((g) => {
          const st = triageStatus(g);
          return st === 'help' || st === 'shaky';
        })
        .map((g) => g.label)
        .slice(0, 2),
    [subjectHealth]
  );

  // Top struggling topics: most-struggled first, take up to five with strugglers.
  const topStruggling = useMemo(
    () =>
      [...topics]
        .filter((t) => t.struggled > 0)
        .sort((a, b) => b.struggled - a.struggled)
        .slice(0, 5),
    [topics]
  );

  // Students who need eyes on them this week: needs-help first, then shaky,
  // most-struggled first inside each. Powers the "Needs attention" card.
  const attentionStudents = useMemo(() => {
    if (!hasStudents) return [];
    const rank: Record<string, number> = { notyet: 0, partly: 1 };
    return sortedStudents
      .filter((s) => {
        const b = studentHealth(s);
        return b === 'notyet' || b === 'partly';
      })
      .sort((a, b) => {
        const r = (rank[studentHealth(a)] ?? 9) - (rank[studentHealth(b)] ?? 9);
        return r !== 0 ? r : b.struggled_count - a.struggled_count;
      })
      .slice(0, 4);
  }, [hasStudents, sortedStudents]);

  // Students who haven't had a single curiosity session — invisible in every
  // topic-derived number above, but exactly who a teacher needs to nudge.
  const notStarted = useMemo(
    () => (students ?? []).filter((s) => (s.topics || []).length === 0),
    [students]
  );

  // "What to do next" — the report's numbers turned into concrete moves, most
  // urgent first: whole-class recaps for systemic topics, the top student to
  // check in with, and who still needs a first session. Max four lines.
  const nextActions = useMemo(() => {
    const acts: { key: string; text: React.ReactNode }[] = [];
    topics
      .filter((t) => t.flagged)
      .slice(0, 2)
      .forEach((t) => {
        acts.push({
          key: `recap-${t.topic}`,
          text: (
            <>
              Recap <b>{t.topic}</b> with the whole class — {t.struggled} of {t.n_students} struggled.
            </>
          ),
        });
      });
    const worst = attentionStudents[0];
    if (worst) {
      acts.push({
        key: `checkin-${worst.student_id}`,
        text: (
          <>
            Check in with <b>{worst.name}</b>
            {worst.struggled_count > 0
              ? ` — struggling with ${worst.struggled_count} topic${worst.struggled_count > 1 ? 's' : ''}.`
              : ' — partly there on this term.'}
          </>
        ),
      });
    }
    if (notStarted.length > 0) {
      const names = notStarted.slice(0, 4).map((s) => s.name).join(', ');
      acts.push({
        key: 'not-started',
        text: (
          <>
            Get <b>{names}</b>
            {notStarted.length > 4 ? ` and ${notStarted.length - 4} more` : ''} a first curiosity
            session — no data on them yet.
          </>
        ),
      });
    }
    if (flags.length > 0) {
      acts.push({
        key: 'reinforcing',
        text: (
          <>
            The coach is reinforcing <b>{flags.length}</b> flagged topic
            {flags.length > 1 ? 's' : ''} — check back next week for movement.
          </>
        ),
      });
    }
    return acts.slice(0, 4);
  }, [topics, attentionStudents, notStarted, flags.length]);

  const handleDownload = useCallback(() => {
    if (!data || !hasStudents || !health) {
      flashDownloadNote('No report to download yet — hit Refresh to build it first.');
      return;
    }
    const h = health;
    const today = new Date().toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    const classLine = [
      school,
      grade != null ? `Grade ${grade}` : null,
      section ? `Section ${section}` : null,
    ]
      .filter(Boolean)
      .join(' · ');

    const VIOLET: [number, number, number] = [124, 58, 237];
    const INK: [number, number, number] = [15, 23, 42];
    const MUTED: [number, number, number] = [100, 116, 139];
    const BORDER: [number, number, number] = [233, 230, 244];
    const STATUS_RGB: Record<string, [number, number, number]> = {
      'On track': [4, 120, 87],
      Shaky: [180, 83, 9],
      'Needs help': [190, 18, 60],
      Exploring: [100, 116, 139],
    };
    const studentStatusLabel = (st: ReinforcementStudent) => {
      const b = rollupBucket((st.topics || []).map((t) => t.state));
      return b === 'got' ? 'On track' : b === 'partly' ? 'Shaky' : b === 'notyet' ? 'Needs help' : 'Exploring';
    };

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const M = 40;
    const contentW = pageW - M * 2;
    let y = M + 6;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...VIOLET);
    doc.text('CURIOSITY COACH · CLASS REPORT', M, y);
    y += 20;
    doc.setFontSize(20);
    doc.setTextColor(...INK);
    doc.text(classLine, M, y);
    y += 16;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    doc.text(`Generated ${today} · Private to the teacher`, M, y);
    y += 22;

    // Glance box — takeaway only (the tiles right below carry the numbers).
    const glance =
      attentionSubjects.length > 0
        ? `${attentionSubjects.join(' and ')} need${attentionSubjects.length === 1 ? 's' : ''} the most attention.`
        : '';
    if (glance) {
      doc.setFontSize(11);
      const glanceLines = doc.splitTextToSize(glance, contentW - 24) as string[];
      const glanceH = glanceLines.length * 15 + 20;
      doc.setFillColor(246, 242, 255);
      doc.setDrawColor(...BORDER);
      doc.roundedRect(M, y, contentW, glanceH, 8, 8, 'FD');
      doc.setTextColor(...INK);
      doc.text(glanceLines, M + 12, y + 20);
      y += glanceH + 16;
    }

    // Three tiles
    const tiles: { k: string; v: number; c: [number, number, number] }[] = [
      { k: 'GOT IT', v: h.got, c: [4, 120, 87] },
      { k: 'PARTLY THERE', v: h.partly, c: [180, 83, 9] },
      { k: 'NOT YET', v: h.notyet, c: [190, 18, 60] },
    ];
    const gap = 12;
    const tw = (contentW - gap * 2) / 3;
    tiles.forEach((t, i) => {
      const tx = M + i * (tw + gap);
      doc.setDrawColor(...BORDER);
      doc.roundedRect(tx, y, tw, 58, 8, 8, 'S');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...MUTED);
      doc.text(t.k, tx + 12, y + 18);
      doc.setFontSize(22);
      doc.setTextColor(...t.c);
      doc.text(String(t.v), tx + 12, y + 44);
    });
    y += 58 + 24;

    const sectionTitle = (label: string) => {
      if (y > pageH - 90) {
        doc.addPage();
        y = M + 6;
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...VIOLET);
      doc.text(label.toUpperCase(), M, y);
      y += 8;
    };

    const colourStatusCells = (hookData: any) => {
      if (hookData.section !== 'body') return;
      const c = STATUS_RGB[String(hookData.cell.raw ?? '').trim()];
      if (c) {
        hookData.cell.styles.textColor = c;
        hookData.cell.styles.fontStyle = 'bold';
      }
    };
    const commonTable = {
      margin: { left: M, right: M },
      theme: 'striped' as const,
      headStyles: { fillColor: [243, 239, 254] as [number, number, number], textColor: [71, 85, 105] as [number, number, number], fontStyle: 'bold' as const, fontSize: 8 },
      bodyStyles: { fontSize: 9, textColor: INK, cellPadding: 6 },
      alternateRowStyles: { fillColor: [251, 250, 254] as [number, number, number] },
      didParseCell: colourStatusCells,
    };
    const afterTable = () => {
      y = (doc as any).lastAutoTable.finalY + 22;
    };

    if (subjectHealth.length > 0) {
      sectionTitle('By subject');
      autoTable(doc, {
        ...commonTable,
        startY: y,
        head: [['Subject', 'Got', 'Partly', 'Not yet', 'Status']],
        body: subjectHealth.map((g) => [
          `${g.label}  (${NCERT_SUBJECTS.has(g.label) ? 'NCERT' : 'STEM'})`,
          g.got,
          g.partly,
          g.notyet,
          STATUS_META[triageStatus(g)].label,
        ]),
        columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
      });
      afterTable();
    }

    if (topStruggling.length > 0) {
      sectionTitle('Top struggling topics');
      autoTable(doc, {
        ...commonTable,
        startY: y,
        head: [['Topic', 'Struggling']],
        body: topStruggling.map((t) => [t.topic, `${t.struggled} / ${t.n_students}`]),
        columnStyles: { 1: { halign: 'right', cellWidth: 90 } },
      });
      afterTable();
    }

    if ((students || []).length > 0) {
      sectionTitle('By student');
      autoTable(doc, {
        ...commonTable,
        startY: y,
        head: [['Student', 'Category', 'Status', 'Topics', 'Gaps']],
        body: (students || []).map((st) => [
          st.name,
          st.category,
          studentStatusLabel(st),
          (st.topics || []).map((t) => t.topic).join(', ') || '—',
          (st.gaps || []).join(', ') || '—',
        ]),
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 78 },
          1: { cellWidth: 70 },
          2: { cellWidth: 66 },
        },
      });
      afterTable();
    }

    const fname =
      ('Class report - ' + classLine).replace(/[^\w\s-]/g, '').replace(/\s+/g, ' ').trim() + '.pdf';
    doc.save(fname);
    flashDownloadNote('Your report has been downloaded as a PDF.');
  }, [
    data,
    hasStudents,
    health,
    subjectHealth,
    attentionSubjects,
    topStruggling,
    students,
    school,
    grade,
    section,
    flashDownloadNote,
  ]);

  return (
    <TeacherLayout active="report">
    <div className="mx-auto max-w-6xl">
      {/* Header: identity left, actions right — one row. */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-slate-400">
            {[school, grade != null ? `Grade ${grade}` : null, section ? `Section ${section}` : null]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">Class Report</h1>
          <p className="mt-1 max-w-[62ch] text-sm text-slate-500">
            <span className="font-semibold text-slate-700">Private to you</span> — never shown to
            students.
          </p>
        </div>
        {section2 === 'report' && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => load(true)}
              disabled={loading}
              className="rounded-xl border border-[#E9E6F4] bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:border-[#D9D3EE] disabled:opacity-50"
            >
              {loading ? 'Working…' : '↻ Refresh'}
            </button>
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-700"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M12 3v12M8 11l4 4 4-4M4 21h16" />
              </svg>
              Download report
            </button>
          </div>
        )}
      </div>

      {section2 === 'report' && downloadNote && (
        <div className="mt-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-2.5 text-sm font-medium text-violet-700">
          {downloadNote}
        </div>
      )}

      {/* ONE control row: report/analytics tabs left, report window right. */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="inline-flex rounded-lg bg-slate-100 p-1 text-sm">
          <button
            type="button"
            onClick={() => setSection2('report')}
            className={`rounded-md px-4 py-1.5 font-medium transition-colors ${
              section2 === 'report'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Student report
          </button>
          <button
            type="button"
            onClick={() => setSection2('analytics')}
            className={`rounded-md px-4 py-1.5 font-medium transition-colors ${
              section2 === 'analytics'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Class analytics
          </button>
        </div>
        {section2 === 'report' && (
          <DateRangeControl value={reportRange} onChange={setReportRange} includeAllTime />
        )}
      </div>

      {section2 === 'analytics' && (
        <div className="mt-6">
          {school && gradeNum != null && !Number.isNaN(Number(gradeNum)) ? (
            <ClassAnalytics school={school} grade={Number(gradeNum)} section={section ?? null} />
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-600">
              Pick a class from Classes to see analytics.
            </div>
          )}
        </div>
      )}

      {section2 === 'report' && (
      <>
      {error && <div className="mt-4 rounded-lg bg-red-50 p-4 text-red-700">{error}</div>}

      {grade == null && (
        <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-600">
          Pick a class from Classes to see its report.
        </div>
      )}

      {loading && !resp && grade != null && <div className="mt-8 text-slate-500">Building the report…</div>}

      {isEmpty && !loading && !error && (
        <div className="mt-8 rounded-lg bg-slate-50 p-6 text-slate-600">
          No concept data yet for this class — students need a few curiosity sessions before struggles show up.
        </div>
      )}

      {topics.length > 0 && (
        <>
          {/* ── KPI row — the two numbers a teacher acts on ── */}
          {health && (
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:max-w-xl">
              <KpiCard
                glyph="users"
                chip="bg-violet-100 text-violet-700"
                label="Students"
                value={data?.class.size ?? health.total}
                sub={`${data?.class.students_with_data ?? health.total} with data`}
              />
              <KpiCard
                glyph="flag"
                chip="bg-violet-100 text-violet-700"
                label="Reinforcing"
                value={flags.length}
                sub="topics flagged to focus"
              />
            </div>
          )}

          {/* One-line takeaway — plain, no gradient. */}
          {(attentionSubjects.length > 0 ||
            (trend?.week_delta_got_pct != null && trend.week_delta_got_pct !== 0)) && (
            <div className="mt-3 flex items-center gap-2.5 rounded-2xl border border-[#E9E6F4] bg-white px-4 py-3">
              <span className="h-2 w-2 flex-none rounded-full bg-amber-500" aria-hidden />
              <p className="text-sm font-semibold text-slate-800">
                {attentionSubjects.length > 0 && (
                  <>
                    {attentionSubjects.join(' and ')} need
                    {attentionSubjects.length === 1 ? 's' : ''} the most attention.
                  </>
                )}
                {trend?.week_delta_got_pct != null && trend.week_delta_got_pct !== 0 && (
                  <>
                    {' '}
                    "Got it" is {trend.week_delta_got_pct > 0 ? 'up' : 'down'}{' '}
                    {Math.abs(Math.round(trend.week_delta_got_pct))}% vs last week.
                  </>
                )}
              </p>
            </div>
          )}

          {/* ── What to do next — the report turned into moves for the week ── */}
          {nextActions.length > 0 && (
            <section className="mt-4 rounded-2xl border border-[#E9E6F4] bg-white p-5">
              <h2 className="text-sm font-bold text-slate-900">What to do next</h2>
              <ul className="mt-3 flex flex-col gap-2">
                {nextActions.map((a, i) => (
                  <li key={a.key} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-violet-100 text-[11px] font-bold text-violet-700">
                      {i + 1}
                    </span>
                    <span className="text-sm text-slate-700">{a.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ── Subject performance | Improvement trend ── */}
          <div className="mt-4 grid gap-4 lg:grid-cols-5">
            <section className="rounded-2xl border border-[#E9E6F4] bg-white p-5 lg:col-span-3">
              <h2 className="text-sm font-bold text-slate-900">Subject performance</h2>
              {subjectHealth.length > 0 ? (
                <div className="mt-2 flex flex-col">
                  {subjectHealth.map((g) => {
                    const total = g.got + g.partly + g.notyet + g.explored;
                    const isNcert = NCERT_SUBJECTS.has(g.label);
                    const isOpen = openSubject === g.label;
                    const breakdown = isOpen ? studentsForSubject(students || [], g.label) : [];
                    return (
                      <div key={g.label} className="border-t border-[#F1EEF9] first:border-t-0">
                        <button
                          type="button"
                          onClick={() => setOpenSubject(isOpen ? null : g.label)}
                          aria-expanded={isOpen}
                          className="grid w-full grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2 py-3 text-left transition-colors hover:bg-violet-50/40 sm:grid-cols-[minmax(7rem,1fr)_minmax(8rem,150px)_110px]"
                        >
                          <div>
                            <div className="flex items-center gap-1.5 text-[15px] font-bold text-slate-800">
                              <span
                                aria-hidden
                                className={`text-xs text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                              >
                                ▶
                              </span>
                              {g.label}
                            </div>
                            <div className="mt-0.5 pl-5 text-[11px] font-medium text-slate-400">
                              {isNcert ? 'NCERT' : 'STEM'} · {total} student{total === 1 ? '' : 's'}
                            </div>
                          </div>
                          <div className="order-3 col-span-2 sm:order-none sm:col-span-1">
                            <MiniHealthBar g={g} />
                          </div>
                          <div className="justify-self-end">
                            <StatusPill status={triageStatus(g)} />
                          </div>
                        </button>
                        {isOpen && (
                          <div className="mb-3 ml-5 rounded-xl bg-[#FBFAFE] px-4 py-3">
                            {breakdown.length === 0 ? (
                              <p className="text-sm text-slate-500">No student data for this subject yet.</p>
                            ) : (
                              <ul className="flex flex-col gap-2.5">
                                {breakdown.map((st) => (
                                  <li key={st.student_id} className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm font-semibold text-slate-800">{st.name}</span>
                                    <StatusPill status={st.status} />
                                    <span className="text-xs text-slate-500">
                                      {st.topics.map((t) => `${t.topic} (${t.state})`).join(' · ')}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="mt-3 text-sm text-slate-400">No subject data yet.</p>
              )}
            </section>

            <section className="rounded-2xl border border-[#E9E6F4] bg-white p-5 lg:col-span-2">
              <h2 className="text-sm font-bold text-slate-900">Improvement trend</h2>
              {trend ? (
                <TrendWidget trend={trend} />
              ) : (
                <p className="mt-3 text-sm text-slate-400">
                  Weekly trend builds up as the class keeps using the curiosity.
                </p>
              )}
            </section>
          </div>

          {/* ── Needs attention | Top struggling topics ── */}
          {(attentionStudents.length > 0 || topStruggling.length > 0) && (
            <div className={`mt-4 grid gap-4 ${attentionStudents.length > 0 && topStruggling.length > 0 ? 'lg:grid-cols-2' : ''}`}>
              {attentionStudents.length > 0 && (
                <section className="rounded-2xl border border-[#E9E6F4] bg-white p-5">
                  <h2 className="text-sm font-bold text-slate-900">Students needing attention</h2>
                  <ul className="mt-3 flex flex-col gap-2.5">
                    {attentionStudents.map((s, i) => {
                      const b = studentHealth(s);
                      const status: TriageStatus = b === 'notyet' ? 'help' : 'shaky';
                      return (
                        <li key={s.student_id} className="flex items-center gap-3">
                          <span className="w-4 text-center text-xs font-bold tabular-nums text-slate-400">
                            {i + 1}
                          </span>
                          <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-700">
                            {s.name.charAt(0).toUpperCase()}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold text-slate-800">{s.name}</span>
                            <span className="block text-xs text-slate-400">
                              {s.struggled_count > 0
                                ? `struggling with ${s.struggled_count} topic${s.struggled_count > 1 ? 's' : ''}`
                                : 'partly there on this term'}
                            </span>
                          </span>
                          <StatusPill status={status} />
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {topStruggling.length > 0 && (
                <section className="rounded-2xl border border-[#E9E6F4] bg-white p-5">
                  <h2 className="text-sm font-bold text-slate-900">Top struggling topics</h2>
                  <div className="mt-3 flex flex-col gap-2.5">
                    {topStruggling.map((t) => {
                      const denom = t.n_students > 0 ? t.n_students : 1;
                      const frac = Math.min(1, t.struggled / denom);
                      const barColor =
                        frac >= 0.5 ? 'bg-rose-400' : frac >= 0.25 ? 'bg-amber-500' : 'bg-emerald-500';
                      const countColor =
                        frac >= 0.5 ? 'text-rose-600' : frac >= 0.25 ? 'text-amber-600' : 'text-emerald-600';
                      return (
                        <div key={t.topic} className="flex items-center gap-3">
                          <span className="min-w-0 flex-[0_0_42%] truncate text-sm font-semibold text-slate-700">
                            {t.topic}
                          </span>
                          <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#E9E6F4]">
                            <span
                              className={`block h-full rounded-full ${barColor}`}
                              style={{ width: `${Math.max(6, Math.round(frac * 100))}%` }}
                            />
                          </span>
                          <span
                            className={`w-24 flex-none whitespace-nowrap text-right font-mono text-xs font-semibold ${countColor}`}
                          >
                            {t.struggled} / {t.n_students}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
          )}

          {/* ── Reinforce — jump to the class list to review & flag students ── */}
          <button
            type="button"
            onClick={() => navigate('/class-details', { state: { school, grade, section } })}
            className="mt-6 flex w-full items-center gap-4 rounded-2xl border border-[#E9E6F4] bg-white p-5 text-left transition-colors hover:border-violet-200 hover:bg-violet-50/40"
          >
            <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-violet-100 text-violet-700">
              <Glyph d={GLYPHS.flag} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-bold text-slate-900">Reinforce a student</span>
              <span className="mt-0.5 block text-xs text-slate-500">
                Open the class list to review each student and flag topics for the coach to reinforce.
              </span>
            </span>
            <span aria-hidden className="text-lg text-slate-400">→</span>
          </button>

          {resp?.computed_at && (
            <p className="mt-4 text-xs text-slate-400">
              Generated {new Date(resp.computed_at).toLocaleString()}
            </p>
          )}
        </>
      )}
      </>
      )}
    </div>
    </TeacherLayout>
  );
};

export default ClassReinforcement;
